<template>
  <header :class="['header', { 'header--mobile': isMobile }]">
    <router-link :to="{ name: 'Home' }">
      <div class="header__logo">
        <!--AllTeams-->
        <img src="@/assets/AllTeams_space_logo.png" alt="AllTeams.space logo" />
      </div>
    </router-link>

    <div class="header__controls">
      <div class="header__login-button" v-if="!currentUser?.userId">
        <router-link :to="{ name: 'Login' }">
          <el-button plain>Sign in</el-button>
        </router-link>
      </div>

      <div class="header__notifications" v-if="currentUser.userId">
        <router-link :to="{ name: 'Notifications' }">
          <el-badge :value="notificationCount" class="header__notifications-badge" size="default" :hidden="notificationCount === 0">
            <el-button circle>
              <el-icon><Bell/></el-icon>
            </el-button>
          </el-badge>
        </router-link>
      </div>

      <router-link v-if="currentUser.userId" :to="{
        name: 'UserProfile',
        params: { link: currentUserLink },
      }">
        <div class="header__user">
          <el-avatar class="header__avatar" size="default" :alt="userName">
            <user-avatar :userId="currentUser.userId" />
          </el-avatar>

          <div class="header__user-name">
            {{ userName }}
          </div>
        </div>
      </router-link>

      <el-popover ref="userPopover" placement="bottom-end" :width="isMobile ? 260 : 160" trigger="click"
        v-model:visible="isMenuOpen" v-if="currentUser.userId">
        <div class="header__user-menu user-menu">
          <main-menu :is-mobile="isMobile" v-if="isMobile" />
          <router-link class="user-menu__link" :to="{
            name: 'UserProfile',
            params: { link: currentUserLink },
            query: { edit: true },
          }">
            Edit profile
          </router-link>
          <router-link class="user-menu__link" :to="{
            name: 'InviteFriend',
          }">
            Invite a friend
          </router-link>
          <a class="user-menu__link" @click="logout">
            Log out
          </a>
        </div>

        <template #reference>
          <span>
            <el-button circle @click="togglePopover" size="default">
              <el-icon v-if="!isMobile" class="main-menu__item-icon">
                <ArrowDown/>
              </el-icon>
            </el-button>
          </span>
        </template>
      </el-popover>

    </div>
  </header>
</template>

<script>
import MainMenu from '@/components/MainMenu.vue';
import {
  AUTH_LOGOUT,
  UPDATE_USER_NOTIFICATIONS,
} from '@/store/mutationTypes.js';
import { DESKTOP_SIZE } from '@/settings.js';
import UserAvatar from './profile/UserAvatar.vue';

export default {
  name: 'TheHeader',
  props: {
    isMobile: {
      type: Boolean,
      required: false,
    },
  },
  components: {
    MainMenu,
    UserAvatar,
  },
  data() {
    return {
      notificationUpdateTimer: null,
      isMenuOpen: false,
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.user;
    },
    currentUserLink() {
      const { currentUser } = this;
      return currentUser.userContactInfo
        ? currentUser.userContactInfo.link
        : null;
    },
    userName() {
      const { userContactInfo } = this.currentUser || null;
      return userContactInfo ? userContactInfo.firstName : '';
    },
    notificationCount() {
      return this.$store.state.notifications.newNotificationCount;
    },
    currentRouteName() {
      return this.$route.name;
    },
  },
  watch: {
    currentRouteName() {
      this.isMenuOpen = false;
    },
  },
  methods: {
    logout() {
      this.$store.commit(AUTH_LOGOUT);
      clearTimeout(this.notificationUpdateTimer);
      this.notificationUpdateTimer = null;
    },
    setNotificationUpdating() {
      this.$store.dispatch(UPDATE_USER_NOTIFICATIONS);

      this.notificationUpdateTimer = setTimeout(() => {
        this.setNotificationUpdating();
      }, 180000);
    },
    handleResize() {
      this.isMenuOpen = this.isMenuOpen
        ? window.innerWidth <= DESKTOP_SIZE
        : this.isMenuOpen;
    },
    togglePopover() {
      //console.log('togglePopover');
    },
  },
  created() {
    if (this.currentUser.userId) {
      this.setNotificationUpdating();
    }
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener('resize', this.handleResize);
      this.handleResize();
    });
  },
  beforeDestroy() {
    clearTimeout(this.notificationUpdateTimer);
    this.notificationUpdateTimer = null;
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style lang="scss" scoped>
@import '~@/styles/shared/_globals.scss';

.header {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: 100;

  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  box-sizing: border-box;
  padding: 10px 14px;

  height: var(--header-height--mobile);
  background-color: var(--color-primary-600);

  @include from('xl') {
    height: var(--header-height);
    padding: 14px 30px;
  }

  &__logo {
    font-size: 28px;
    line-height: 1.4;
    font-weight: 600;
    color: var(--color-gray-50);

    @include from('xl') {
      font-size: 42px;
      line-height: 1;
    }
    height: 90%;
    width: 90%;
    display: flex;
  }

  &__controls {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
  }

  &__notifications {
    margin-right: 20px;
  }

  &__notifications-badge {}

  &__user {
    flex-flow: row nowrap;
    display: flex;
    align-items: center;
    margin-right: 10px;

    background-color: var(--color-gray-50);
    border-radius: 139px;
    padding: 2px;
  }

  &__avatar {}

  &__user-name {
    margin: 0 10px 0 6px;
    color: var(--color-gray-900);
    font-size: var(--font-size-100);
  }

  &__user-menu {}
}

.user-menu {
  &__link {
    display: block;
    color: var(--color-gray-900);
    cursor: pointer;
    padding: var(--global-dimension-size-75) 0 var(--global-dimension-size-150);
    font-size: var(--font-size-200);

    &:last-of-type {
      padding-bottom: 0;
    }

    &:hover {
      color: var(--color-primary-400);
    }

    @include from('xl') {
      font-size: var(--font-size-200);
      padding: 8px 6px;
    }
  }
}
</style>
